import React, { useMemo } from "react";

import styled, { css } from "styled-components";

import { SpaceComponentObject } from "../../../../../types";
import DropTarget from "../../../layout/DropTarget/DropTarget";
import { ChildLayoutContext } from "../../../layout/LayoutContext";
import { useStableSpaceContext } from "../../SpaceContext";
import { useIsFirstInstance } from "../contexts/ComponentPathContext";
import SpaceComponent, { Props } from "../SpaceComponent";

const sharedStyles = css`
  display: flex;
  align-items: flex-start;
  height: 100%;
  min-width: 10px;

  & > * {
    min-height: 0;
    max-height: 100%;

    /* 
      For components in panels, like table, which may be very tall a height contraints
      must exist as a direct ancestor of the flex children of the panel. If this height
      constraint is on the direct child of the flex_box all the flex children of the 
      flex_box would attempt to size to its height and thus be treated as having 
      equal height when the flex algorithim allocates space.
      
      --component-height is a bit of a fudge here to keep the "natural" height of descendants
      like tables from dwarfing the height of shorter components and being allocated almost 
      the entire flex height.

      If you change this check that the following case renders reasonably:
      ------------------------
      |  ----------          |
      |  | button |          |
      |  ----------          |
      |  ---------           |
      |  | table |           |
      |  ---------           |
      |  ----------          |
      |  | button |          |
      |  ----------          |
      ------------------------

    */
    & > * {
      height: 100%;
      max-height: var(--component-height);
    }
  }
`;

const Root = styled.div<{
  backgroundColor?: string;
  border?: string;
  borderRadius?: string;
}>`
  ${sharedStyles}

  ${props => props.backgroundColor && `background-color: ${props.backgroundColor};`}
  ${props => props.border && `border: ${props.border};`}
  ${props => props.borderRadius && `border-radius: ${props.borderRadius};`}
`;

const StyledDropTarget = styled(DropTarget)`
  ${sharedStyles}
  width: 100%;
`;

const EditModeRoot = styled(Root)<{ transparentContainer: boolean }>`
  min-width: 20px;
  min-height: 20px;
  ${props =>
    props.transparentContainer ? `border: dashed 1px ${props.theme.borderGrey};` : ""}
`;

export default function SpaceFlexBox({
  spaceComponent: {
    componentTreeNodes,
    properties: { style, theme_properties }
  },
  ...props
}: Props) {
  const { editMode } = useStableSpaceContext();
  const isFirstInstance = useIsFirstInstance();

  const contents = (componentTreeNodes as SpaceComponentObject[]).map(ctn => (
    <SpaceComponent key={ctn.slug} spaceComponent={ctn} {...props} />
  ));

  const { backgroundColor, border, borderRadius } = useMemo(() => {
    const themeProperties = theme_properties || {};

    return {
      backgroundColor: themeProperties["background-color"],
      border: themeProperties["border"],
      borderRadius: themeProperties["border-radius"]
    };
  }, [theme_properties]);

  if (!editMode || !isFirstInstance)
    return (
      <ChildLayoutContext>
        <Root
          style={style}
          backgroundColor={backgroundColor}
          border={border}
          borderRadius={borderRadius}
        >
          {contents}
        </Root>
      </ChildLayoutContext>
    );

  return (
    <ChildLayoutContext>
      <EditModeRoot
        backgroundColor={backgroundColor}
        border={border}
        borderRadius={borderRadius}
        transparentContainer={!border}
      >
        <StyledDropTarget style={style}>{contents}</StyledDropTarget>
      </EditModeRoot>
    </ChildLayoutContext>
  );
}
